/**
 * T218 — public Stream player primitive for catalog-decoupled page videos.
 *
 * Renders a Cloudflare Stream video by `uid` via the account customer-subdomain
 * iframe embed (`customer-<CODE>.cloudflarestream.com/<uid>/iframe`, see
 * `~/lib/stream/embed`): adaptive bitrate, all browsers, no client dependency
 * (no hls.js), Stream-served auto-poster. `title` is the accessible name.
 *
 * The per-locale curated `.vtt` track (subtitle_key, stored + managed in the BO) is
 * the SEO/a11y caption source; surfacing it through Stream's caption API so the
 * iframe renders it is a thin documented follow-up — out of this video-first slice.
 *
 * This is the raw primitive; T219 owns the typed slot registry + `usePageAsset`
 * + placeholder fallback that surfaces consume.
 */

import { streamEmbedUrl } from "~/lib/stream/embed";

export type PageVideoProps = {
  /** Cloudflare Stream video uid. */
  uid: string;
  /** Accessible name for the player (also the iframe title). */
  title: string;
  className?: string;
};

export function PageVideo({ uid, title, className }: PageVideoProps) {
  return (
    <div
      className={`relative aspect-video w-full overflow-hidden ${className ?? ""}`}
      style={{ borderRadius: "var(--tfc-radius-lg)" }}
    >
      <iframe
        src={streamEmbedUrl(uid)}
        title={title}
        loading="lazy"
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen;"
        allowFullScreen
        className="absolute inset-0 h-full w-full border-0"
      />
    </div>
  );
}
